import {Component, computed, effect, ElementRef, inject, signal, ViewChild} from '@angular/core';
import { AssetVersionPipe } from '../../../../pipes/asset-version.pipe';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { PolarisRoutePaths, RoutePaths } from '../../../../shared/types/common';
import { POLARIS_NAME } from '../../copilot.model';
import { MatTooltip } from '@angular/material/tooltip';
import { tooltips } from '../../../../utils/tooltips';
import {PolarisRuntimeFacadeService} from '../../../../services/polaris/polaris-runtime-facade.service';
import {PolarisChatHistoryItemVm} from '../../../../services/polaris/model';

@Component({
  selector: 'app-chat-history',
  standalone: true,
  imports: [CommonModule, MatTooltip, AssetVersionPipe],
  templateUrl: './chat-history.component.html',
  styleUrl: './chat-history.component.scss'
})
export class ChatHistoryComponent {
  private polarisRuntimeFacadeService = inject(PolarisRuntimeFacadeService);
  private router = inject(Router);
  public polarisName = POLARIS_NAME;
  private hoverTimeout: any;
  private movedToChat = false;
  private isLoadingMore = false;

  protected readonly tooltips = tooltips;
  public scrolledToBottom = false;
  public isScrollable = false;
  public showHistory = false;
  public searchTerm = signal('');
  public isLoadingOverview = computed(() => this.polarisRuntimeFacadeService.state.isLoadingOverview());
  public canLoadMoreChats = computed(() => this.polarisRuntimeFacadeService.state.canLoadMoreChats());
  public chatHistoryItems = computed(() => {
    return [...this.polarisRuntimeFacadeService.state.chatHistoryItems()]
      .sort((a, b) => b.lastUpdate - a.lastUpdate);
  });
  public filteredChats = computed(() => {
    const search = this.searchTerm().toLowerCase();
    const allChats = this.chatHistoryItems();

    if (!search) {
      return allChats;
    }

    return allChats.filter(chat =>
      chat.title.toLowerCase().includes(search)
    );
  });
  public noChatsMessage = computed(() => {
    return this.searchTerm() ? 'No chats match your search' : 'Your chat history is empty';
  });
  public noChatsSubMessage = computed(() => {
    return this.searchTerm() ? 'Try a different term or phrase' : `Ask ${this.polarisName} anything about your brand,`;
  });
  public noChatsExtraMessage = computed(() => {
    return this.searchTerm() ? '' : 'campaigns, or competitors to begin';
  });

  @ViewChild('chatList') chatList!: ElementRef<HTMLDivElement>;

  constructor() {
    effect(() => {
      this.filteredChats();
      this.isLoadingOverview();
      queueMicrotask(() => this.updateScrollable());
    });
  }

  onScroll() {
    this.updateScrollable();
    void this.tryLoadMoreChats();
  }

  updateScrollable() {
    const el = this.chatList?.nativeElement;
    if (!el) return;

    const threshold = 2;
    const scrollable = el.scrollHeight > el.clientHeight;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;

    this.isScrollable = scrollable;
    this.scrolledToBottom = atBottom;
  }

  public async navigateToChat(chatId: string): Promise<void> {
    if (this.router.url.includes(chatId)) return;
    this.movedToChat = true;
    const result = await this.polarisRuntimeFacadeService.openChat(chatId);
    if (result.found) {
      await this.router.navigate([`${RoutePaths.POLARIS}/${PolarisRoutePaths.CHAT}/${chatId}`]);
      return;
    }

    await this.router.navigate([`${RoutePaths.POLARIS}/${PolarisRoutePaths.POLARIS_HOME}`]);
  }

  public navigateToHome(): void {
    this.showHistory = false;
    this.polarisRuntimeFacadeService.resetRecentChatId();
    this.router.navigate([`${RoutePaths.POLARIS}/${PolarisRoutePaths.POLARIS_HOME}`]);
  }

  public onSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchTerm.set(target.value);
  }

  public isCurrentChat(chatId: string): boolean {
    const currentUrl = this.router.url;
    return currentUrl.includes(`/c/${chatId}`);
  }

  public toggleShowHistory(): void {
    this.movedToChat = false;
    this.showHistory = !this.showHistory;
    this.updateScrollable();
    if (this.showHistory) {
      void this.ensureHistoryLoaded();
    }
  }

  get isHomePage(): boolean {
    return this.router.url.includes(PolarisRoutePaths.POLARIS_HOME);
  }

  get isAlertCenterPage(): boolean {
    return this.router.url.includes(PolarisRoutePaths.ALERTS);
  }

  public trackByChatId(index: number, chat: PolarisChatHistoryItemVm): string {
    return chat.id;
  }

  public onEnterHistory() {
    clearTimeout(this.hoverTimeout);
    this.showHistory = true;
  }

  public onLeaveHistory() {
    if (this.movedToChat) {
      this.hoverTimeout = setTimeout(() => {
        this.showHistory = false;
      }, 1000);
    }
  }

  private async ensureHistoryLoaded(): Promise<void> {
    if (this.chatHistoryItems().length > 0 || this.isLoadingOverview()) {
      return;
    }
    await this.polarisRuntimeFacadeService.loadChatsOverview();
  }

  private async tryLoadMoreChats(): Promise<void> {
    if (this.isLoadingMore || this.isLoadingOverview() || !this.canLoadMoreChats()) {
      return;
    }
    if (this.searchTerm().trim().length > 0) {
      return;
    }

    const el = this.chatList?.nativeElement;
    if (!el) {
      return;
    }

    const threshold = 24;
    const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;
    if (!isNearBottom) {
      return;
    }

    this.isLoadingMore = true;
    try {
      await this.polarisRuntimeFacadeService.loadMoreChats();
    } finally {
      this.isLoadingMore = false;
      this.updateScrollable();
    }
  }
}
